import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/DetailedSaleForm.css';
import { jsPDF } from 'jspdf';

const DetailedSaleForm = ({ onSaleAdded, onCancel }) => {
  const { user } = useAuth();
  const [businessDateTime, setBusinessDateTime] = useState(null);
  const [formData, setFormData] = useState({
    sale_date: new Date().toISOString().split('T')[0], // Fecha actual por defecto
    total_amount: '',
    payment_method: 'Efectivo', // Valor por defecto
    point_of_sale_id: '',
    customer_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pointsOfSale, setPointsOfSale] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productDiscount, setProductDiscount] = useState(0.00);
  const [generalDiscount, setGeneralDiscount] = useState(0.00);
  const [taxRate, setTaxRate] = useState(0.00);
  const [taxAmount, setTaxAmount] = useState(0.00);
  const [subtotal, setSubtotal] = useState(0.00);
  const [totalBeforeTax, setTotalBeforeTax] = useState(0.00);
  const [grandTotal, setGrandTotal] = useState(0.00);
  const [loadingPointsOfSale, setLoadingPointsOfSale] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [printTicket, setPrintTicket] = useState(false);
  const [isGeneratingTicket, setIsGeneratingTicket] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Función para obtener la fecha y hora del negocio desde el servidor
  const fetchBusinessTime = async (businessId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No se encontró token de autenticación');
        return null;
      }

      const response = await axios.get(`/api/sales/server-time?business_id=${businessId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        },
        timeout: 5000 // Timeout de 5 segundos para evitar bloqueos
      });

      console.log('Respuesta completa del servidor:', response.data);

      if (response.data && response.data.time) {
        console.log('Fecha y hora del negocio obtenida:', response.data.time);
        setBusinessDateTime(response.data.time);
        return response.data.time;
      } else {
        console.warn('La respuesta del servidor no contiene fecha/hora');
        return null;
      }
    } catch (error) {
      console.error('Error al obtener fecha/hora del negocio:', error);
      return null;
    }
  };

  // Función para generar el ticket de venta detallada en PDF
  const generateTicket = async (saleData) => {
    try {
      console.log('Iniciando generación de ticket PDF para venta detallada...');
      setIsGeneratingTicket(true);
      let formattedDate = '';
      let formattedTime = '';
      let businessTimezone = 'America/Mexico_City';
      
      // Obtener el business_id del punto de venta seleccionado
      let businessId = null;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      if (selectedPointOfSale && selectedPointOfSale.business_id) {
        businessId = selectedPointOfSale.business_id;
        console.log('Business ID para obtener hora:', businessId);
      }
      
      // Intentar obtener la hora del servidor
      try {
        console.log('Intentando obtener la hora del servidor...');
        const serverTimeResponse = await axios.get('/api/sales/server-time', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 5000
        });
        
        console.log('Respuesta de server-time:', serverTimeResponse.data);
        
        const serverTimeStr = serverTimeResponse.data.time;
        businessTimezone = serverTimeResponse.data.timezone || 'America/Mexico_City';
        
        try {
          console.log('Fecha y hora recibida del servidor:', serverTimeStr);
          
          if (!serverTimeStr || typeof serverTimeStr !== 'string') {
            throw new Error('Formato de fecha inválido recibido del servidor');
          }
          
          // Extraer directamente las partes de la fecha y hora
          const dateParts = serverTimeStr.split('T');
          
          if (dateParts.length !== 2) {
            throw new Error('Formato de fecha/hora inesperado');
          }
          
          // Extraer la fecha (YYYY-MM-DD)
          const rawDate = dateParts[0];
          const dateComponents = rawDate.split('-');
          if (dateComponents.length !== 3) {
            throw new Error('Formato de fecha inesperado');
          }
          
          // Formatear la fecha como DD/MM/YYYY
          formattedDate = `${dateComponents[2]}/${dateComponents[1]}/${dateComponents[0]}`;
          
          // Extraer la hora (HH:MM:SS)
          const rawTime = dateParts[1].split('.')[0]; // Eliminar milisegundos y zona horaria
          formattedTime = rawTime;
          
          console.log('Fecha extraída:', formattedDate);
          console.log('Hora extraída:', formattedTime);
          console.log('Zona horaria del negocio:', businessTimezone);
          
        } catch (formatError) {
          console.error('Error al extraer la fecha y hora:', formatError);
          throw new Error('Error al procesar la fecha del servidor');
        }
      } catch (serverTimeError) {
        console.error('Error al obtener la hora del servidor:', serverTimeError);
        console.log('Usando fecha y hora local como alternativa...');
        
        // Usar la fecha y hora local como respaldo
        const now = new Date();
        formattedDate = now.toLocaleDateString('es-MX', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        });
        formattedTime = now.toLocaleTimeString('es-MX', { 
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false 
        });
        
        console.log('Fecha local (respaldo):', formattedDate);
        console.log('Hora local (respaldo):', formattedTime);
      }
      
      // Obtener datos del negocio asociado al punto de venta seleccionado
      let business = {};
      let currentUser = {};
      
      // Verificar si tenemos el punto de venta seleccionado y su business_id
      if (!selectedPointOfSale || !selectedPointOfSale.business_id) {
        console.error('No se encontró el punto de venta seleccionado o su business_id');
      } else {
        // Tenemos el punto de venta y su business_id, obtener datos del negocio
        try {
          console.log('Intentando obtener datos del negocio con ID:', selectedPointOfSale.business_id);
          const businessDetailResponse = await axios.get(`/api/businesses/${selectedPointOfSale.business_id}`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            timeout: 5000
          });
          
          if (businessDetailResponse.data) {
            business = businessDetailResponse.data;
            console.log('Datos del negocio obtenidos:', business);
          }
        } catch (businessError) {
          console.error('Error al obtener datos del negocio:', businessError);
        }
      }
      
      // Obtener datos del usuario actual
      try {
        const userResponse = await axios.get('/api/users/me', {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        });
        currentUser = userResponse.data;
        console.log('Datos del usuario obtenidos:', currentUser);
      } catch (userError) {
        console.error('Error al obtener datos del usuario:', userError);
        // Continuar con valores predeterminados
        currentUser = { full_name: 'Usuario' };
      }
      
      // Crear el documento PDF con ancho de 80mm (ticket)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 300] // Ancho de 80mm, altura ajustable
      });
      
      doc.setFont('helvetica');
      doc.setFontSize(10);
      
      // Encabezado del ticket
      doc.setFontSize(10);
      doc.text(business.bis_name || 'Negocio', 40, 10, null, null, 'center');
      doc.text('Ticket de Venta', 40, 15, null, null, 'center');
      doc.text(`Tel: ${business.bis_phone || 'No disponible'}`, 40, 20, null, null, 'center');
      doc.text('Venta Detallada', 40, 25, null, null, 'center');
      
      // Información de la venta
      const displayPosName = selectedPointOfSale ? selectedPointOfSale.pos_name : 'No disponible';
      
      doc.text(`Punto de venta: ${displayPosName}`, 2, 35);
      // Truncar el nombre del usuario a 20 caracteres
      const userName = currentUser && currentUser.full_name ? currentUser.full_name.substring(0, 20) : 'No disponible';
      doc.text(`Vendedor: ${userName}`, 2, 40);
      doc.text(`Fecha: ${formattedDate || 'No disponible'}`, 2, 45);
      doc.text(`Hora: ${formattedTime || 'No disponible'} (${businessTimezone || 'UTC'})`, 2, 50);
      
      // Línea divisoria
      doc.line(2, 55, 78, 55);
      doc.setFontSize(9);
      doc.text('Detalle de la venta', 40, 60, null, null, 'center');
      doc.setFontSize(8);
      doc.text('Producto', 2, 65);
      doc.text('Cant.', 45, 65, null, null, 'center');
      doc.text('Subtotal', 75, 65, null, null, 'right');
      
      // Línea divisoria
      doc.line(2, 70, 78, 70);
      
      // Detalles de los productos
      let y = 75;
      saleData.products.forEach(product => {
        // Obtener el nombre del producto, asegurando que existe
        const productName = product.name || product.prod_name || 'Producto';
        
        // Truncar el nombre si es necesario
        const displayName = productName.length > 25 ? productName.substring(0, 22) + '...' : productName;
        
        // Calcular el subtotal si no está definido
        const subtotal = product.subtotal || (product.price * product.quantity) - (product.discount || 0);
        
        doc.text(displayName, 2, y);
        doc.text(product.quantity.toString(), 45, y, null, null, 'center');
        doc.text(`$${subtotal.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      });
      
      // Línea divisoria
      doc.line(2, y, 78, y);
      y += 5;
      
      // Totales
      const subtotalValue = typeof saleData.subtotal === 'number' ? saleData.subtotal : 0;
      doc.text(`Subtotal: $${subtotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Mostrar descuento general siempre, incluso cuando sea 0
      // Asegurar que el valor del descuento sea un número
      let generalDiscountValue = 0;
      if (typeof saleData.generalDiscount === 'number') {
        generalDiscountValue = saleData.generalDiscount;
      } else if (typeof saleData.generalDiscount === 'string' && saleData.generalDiscount !== '' && saleData.generalDiscount !== '.') {
        generalDiscountValue = parseFloat(saleData.generalDiscount);
      } else if (typeof saleData.general_discount === 'number') {
        generalDiscountValue = saleData.general_discount;
      } else if (typeof saleData.general_discount === 'string' && saleData.general_discount !== '' && saleData.general_discount !== '.') {
        generalDiscountValue = parseFloat(saleData.general_discount);
      }
      
      // Asegurar que no sea NaN
      if (isNaN(generalDiscountValue)) generalDiscountValue = 0;
      
      doc.text(`Descuento: $${generalDiscountValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Total antes de impuestos
      const totalBeforeTaxValue = typeof saleData.totalBeforeTax === 'number' ? saleData.totalBeforeTax : 
                                (typeof saleData.total_before_tax === 'number' ? saleData.total_before_tax : subtotalValue - generalDiscountValue);
      doc.text(`Total antes de impuesto: $${totalBeforeTaxValue.toFixed(2)}`, 75, y, null, null, 'right');
      y += 5;
      
      // Impuesto si es mayor a 0
      const taxAmountValue = typeof saleData.taxAmount === 'number' ? saleData.taxAmount : 
                          (typeof saleData.tax_amount === 'number' ? saleData.tax_amount : 0);
      const taxRateValue = typeof saleData.taxRate === 'number' ? saleData.taxRate : 
                         (typeof saleData.tax_rate === 'number' ? saleData.tax_rate : 0);
      
      if (taxAmountValue > 0) {
        doc.text(`Impuesto (${taxRateValue.toFixed(2)}%): $${taxAmountValue.toFixed(2)}`, 75, y, null, null, 'right');
        y += 5;
      }
      
      // Gran total
      doc.setFontSize(10);
      // Asegurar que grandTotal existe y es un número
      const grandTotalValue = typeof saleData.grandTotal === 'number' ? saleData.grandTotal : 
                             (typeof saleData.grand_total === 'number' ? saleData.grand_total : 0);
      doc.text(`Gran Total: $${grandTotalValue.toFixed(2)}`, 75, y, null, null, 'right');
      doc.setFontSize(8);
      y += 10;
      
      // Método de pago
      const paymentMethod = saleData.paymentMethod || saleData.payment_method || 'Efectivo';
      doc.text(`Método de pago: ${paymentMethod}`, 2, y);
      y += 10;
      
      // Pie de página
      doc.text('Gracias por su compra', 40, y, null, null, 'center');
      y += 5;
      
      // Línea en blanco
      y += 5;
      
      // ID de venta
      doc.text('IdVenta', 40, y, null, null, 'center');
      y += 5;
      
      // Generar string de ID venta: fecha+hora+nombre del punto de venta+nombre del vendedor (truncado a 12 caracteres)+monto de venta (sin decimales)
      const dateForId = formattedDate.replace(/\//g, '');
      const timeForId = formattedTime.replace(/:/g, '').replace(/\s/g, '');
      const posName = (selectedPointOfSale?.pos_name || 'POS').replace(/\s+/g, '');
      const sellerName = (currentUser?.full_name || 'Usuario').substring(0, 12).replace(/\s+/g, '');
      const amountForId = Math.floor(grandTotalValue).toString();
      
      const ventaId = `${dateForId}${timeForId}${posName}${sellerName}${amountForId}`;
      doc.text(ventaId, 40, y, null, null, 'center');
      y += 5;
      
      // Generar el nombre del archivo con la fecha y hora
      const dateForFilename = formattedDate.replace(/\//g, '-');
      const timeForFilename = formattedTime.replace(/:/g, '-').replace(/\s/g, '');
      const filename = `ticket_venta_detallada_${dateForFilename}_${timeForFilename}.pdf`;
      
      console.log('Intentando descargar el PDF...');
      
      // Método 1: Método directo de jsPDF (más confiable)
      try {
        console.log('Usando método doc.save() directo');
        doc.save(filename);
        console.log('PDF guardado con método directo');
        setIsGeneratingTicket(false);
        return true;
      } catch (saveError) {
        console.error('Error con método directo:', saveError);
        
        // Si falla el método directo, intentar con el método de Blob
        try {
          const blob = doc.output('blob');
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.style.display = 'none';
          document.body.appendChild(link);
          
          console.log('Descargando con Blob y URL.createObjectURL');
          link.click();
          
          setTimeout(() => {
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            console.log('Limpieza de recursos completada');
          }, 1000);
          
          console.log('PDF descargado con método Blob');
          setIsGeneratingTicket(false);
          return true;
        } catch (blobError) {
          console.error('Error con método Blob:', blobError);
          
          // Método 3: Último recurso, usar output base64 y abrir en nueva ventana
          try {
            const base64 = doc.output('datauristring');
            window.open(base64);
            console.log('PDF abierto en nueva ventana con base64');
            setIsGeneratingTicket(false);
            return true;
          } catch (base64Error) {
            console.error('Error con método base64:', base64Error);
          }
        }
      }
      
      setIsGeneratingTicket(false);
      return false;
    } catch (error) {
      console.error('Error al generar el ticket:', error);
      setIsGeneratingTicket(false);
      return false;
    }
  };

  useEffect(() => {
    const fetchPointsOfSale = async () => {
      try {
        setLoadingPointsOfSale(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/points-of-sale/available', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setPointsOfSale(response.data);
        
        // Si solo hay un punto de venta disponible, seleccionarlo automáticamente
        if (response.data.length === 1) {
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
        } else if (response.data.length > 0) {
          // Si hay múltiples puntos de venta, seleccionar el primero por defecto
          setFormData(prevState => ({
            ...prevState,
            point_of_sale_id: response.data[0].id
          }));
          
          // Obtener la fecha y hora del negocio si hay un punto de venta seleccionado
          if (response.data[0].business_id) {
            fetchBusinessTime(response.data[0].business_id);
          }
        }
      } catch (error) {
        console.error('Error al obtener puntos de venta:', error);
        setError('Error al cargar los puntos de venta. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingPointsOfSale(false);
      }
    };

    const fetchCustomers = async () => {
      try {
        setLoadingCustomers(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/customers', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Filtrar solo clientes activos
        const activeCustomers = response.data.filter(customer => customer.cus_alive);
        setCustomers(activeCustomers);
        
        // Si hay clientes, seleccionar el cliente "Publico en General" por defecto si existe
        if (activeCustomers.length > 0) {
          const publicCustomer = activeCustomers.find(c => c.cus_name === 'Publico en General');
          if (publicCustomer) {
            setFormData(prevState => ({
              ...prevState,
              customer_id: publicCustomer.id
            }));
          } else {
            // Si no existe "Publico en General", seleccionar el primer cliente
            setFormData(prevState => ({
              ...prevState,
              customer_id: activeCustomers[0].id
            }));
          }
        }
      } catch (error) {
        console.error('Error al obtener clientes:', error);
        setError('Error al cargar los clientes. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingCustomers(false);
      }
    };

    const fetchProducts = async () => {
      try {
        setLoadingProducts(true);
        const token = localStorage.getItem('token');
        
        if (!token) {
          setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
          return;
        }

        const response = await axios.get('/api/products', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        // Filtrar solo productos activos
        const activeProducts = response.data.filter(product => product.prod_alive);
        
        // Ordenar alfabéticamente por nombre
        activeProducts.sort((a, b) => a.prod_name.localeCompare(b.prod_name));
        
        setProducts(activeProducts);
        setFilteredProducts(activeProducts);
        
        if (activeProducts.length > 0) {
          setSelectedProductId(activeProducts[0].id);
        }
      } catch (error) {
        console.error('Error al obtener productos:', error);
        setError('Error al cargar los productos. Por favor, inténtelo de nuevo.');
      } finally {
        setLoadingProducts(false);
      }
    };

    fetchPointsOfSale();
    fetchCustomers();
    fetchProducts();
  }, [user]);

  // Calcular el subtotal basado en los productos seleccionados
  useEffect(() => {
    let calculatedSubtotal = 0;
    selectedProducts.forEach(product => {
      // Asegurar que discount sea un número para el cálculo
      const discountValue = typeof product.discount === 'string' ?
        (product.discount === '' || product.discount === '.' ? 0 : parseFloat(product.discount)) :
        product.discount;
      
      const productSubtotal = (product.prod_price * product.quantity) - discountValue;
      calculatedSubtotal += productSubtotal;
    });
    
    setSubtotal(calculatedSubtotal);
  }, [selectedProducts]);

  // Efecto para filtrar productos cuando cambia el término de búsqueda
  useEffect(() => {
    if (!productSearchTerm.trim()) {
      // Si no hay término de búsqueda, mostrar todos los productos
      setFilteredProducts(products);
    } else {
      // Filtrar productos por código o nombre
      const searchTermLower = productSearchTerm.toLowerCase().trim();
      const filtered = products.filter(product => 
        (product.prod_code && product.prod_code.toLowerCase().includes(searchTermLower)) || 
        product.prod_name.toLowerCase().includes(searchTermLower)
      );
      setFilteredProducts(filtered);
      
      // Si hay resultados filtrados, seleccionar el primer producto
      if (filtered.length > 0) {
        setSelectedProductId(filtered[0].id);
      }
    }
  }, [productSearchTerm, products]);

  // Calcular el gran total cuando cambia el subtotal, descuento general o impuesto
  useEffect(() => {
    const taxMultiplier = 1 + (taxRate / 100);
    
    // Asegurar que generalDiscount sea un número para el cálculo
    const discountValue = typeof generalDiscount === 'string' ?
      (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) :
      generalDiscount;
    
    const afterDiscount = Math.max(0, subtotal - discountValue);
    
    // Actualizar el total antes de impuestos
    setTotalBeforeTax(afterDiscount);
    
    // Calcular el monto del impuesto
    const taxAmount = afterDiscount * (taxRate / 100);
    
    // Calcular el total con impuestos
    const calculatedGrandTotal = afterDiscount + taxAmount;
    
    // Actualizar el gran total
    setGrandTotal(calculatedGrandTotal);
    
    // Guardar el monto del impuesto como variable de estado
    setTaxAmount(taxAmount);
    
    // Actualizar el total_amount en formData para enviarlo al servidor
    setFormData(prevState => ({
      ...prevState,
      total_amount: calculatedGrandTotal.toFixed(2)
    }));
  }, [subtotal, generalDiscount, taxRate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleProductChange = (e) => {
    setSelectedProductId(e.target.value);
  };

  const handleProductSearchChange = (e) => {
    setProductSearchTerm(e.target.value);
  };

  const handleQuantityChange = (e) => {
    const value = e.target.value;
  
    // Permitir campo vacío o punto decimal como primer carácter
    if (value === '' || value === '.') {
      setProductQuantity(value);
      return;
    }
  
    // Validar que sea un número válido con máximo 2 decimales
    const regex = /^\d*\.?\d{0,2}$/;
    if (regex.test(value)) {
      setProductQuantity(value);
    }
  };

  const handleDiscountChange = (e) => {
    // Obtener el valor del input y permitir solo números y un solo punto decimal
    let value = e.target.value;
    
    // Permitir entrada vacía o valores parciales como "0." durante la edición
    if (value === '' || value === '.') {
      setProductDiscount(value); // Guardar el valor tal cual para permitir la edición
      return;
    }
    
    // Validar que solo contenga números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      // Si es un valor válido, actualizar el estado
      setProductDiscount(value);
    }
  };

  const handleGeneralDiscountChange = (e) => {
    // Obtener el valor del input y permitir solo números y un solo punto decimal
    let value = e.target.value;
    
    // Permitir entrada vacía o valores parciales como "0." durante la edición
    if (value === '' || value === '.') {
      setGeneralDiscount(value); // Guardar el valor tal cual para permitir la edición
      return;
    }
    
    // Validar que solo contenga números y un punto decimal
    if (/^\d*\.?\d*$/.test(value)) {
      // Si es un valor válido, actualizar el estado
      setGeneralDiscount(value);
    }
  };

  const handleTaxRateChange = (e) => {
    const value = parseFloat(e.target.value);
    if (value >= 0) {
      setTaxRate(value);
    }
  };

  const handleAddProduct = () => {
    if (!selectedProductId) {
      setError('Por favor, seleccione un producto.');
      return;
    }

    // Validar que el campo no esté vacío
    if (productQuantity === '' || productQuantity === '.') {
      setError('Por favor, ingrese una cantidad válida.');
      return;
    }

    // Convertir a número y validar
    const quantityValue = parseFloat(productQuantity);
    if (isNaN(quantityValue) || quantityValue <= 0) {
      setError('La cantidad debe ser mayor a 0.');
      return;
    }

    const product = products.find(p => p.id.toString() === selectedProductId.toString());
    if (!product) {
      setError('Producto no encontrado.');
      return;
    }

    // Convertir el descuento a número para almacenarlo
    const discountValue = productDiscount === '' || productDiscount === '.' ? 
      0 : parseFloat(productDiscount);

    const existingProductIndex = selectedProducts.findIndex(
      p => p.id.toString() === selectedProductId.toString()
    );

    if (existingProductIndex >= 0) {
      const updatedProducts = [...selectedProducts];
      updatedProducts[existingProductIndex].quantity = parseFloat(updatedProducts[existingProductIndex].quantity) + parseFloat(productQuantity);
      updatedProducts[existingProductIndex].discount = discountValue;
      setSelectedProducts(updatedProducts);
    } else {
      setSelectedProducts([
        ...selectedProducts,
        {
          ...product,
          quantity: parseFloat(productQuantity),
          discount: discountValue
        }
      ]);
    }

    setSelectedProductId('');
    setProductQuantity('');
    setProductDiscount('');
    setError('');
  };

  const handleUpdateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      return;
    }
    
    const updatedProducts = selectedProducts.map(product => {
      if (product.id.toString() === productId.toString()) {
        return {
          ...product,
          quantity: parseFloat(newQuantity)
        };
      }
      return product;
    });
    
    setSelectedProducts(updatedProducts);
  };

  const handleUpdateDiscount = (productId, newDiscount) => {
    // Si es un valor vacío o solo un punto, permitir la edición
    if (newDiscount === '' || newDiscount === '.') {
      const updatedProducts = selectedProducts.map(product => {
        if (product.id.toString() === productId.toString()) {
          return {
            ...product,
            discount: newDiscount
          };
        }
        return product;
      });
      
      setSelectedProducts(updatedProducts);
      return;
    }
    
    // Convertir a número para validar
    const discountValue = parseFloat(newDiscount);
    if (isNaN(discountValue) || discountValue < 0) {
      return;
    }
    
    const updatedProducts = selectedProducts.map(product => {
      if (product.id.toString() === productId.toString()) {
        return {
          ...product,
          discount: newDiscount
        };
      }
      return product;
    });
    
    setSelectedProducts(updatedProducts);
  };

  const handleRemoveProduct = (productId) => {
    const updatedProducts = selectedProducts.filter(
      product => product.id.toString() !== productId.toString()
    );
    
    setSelectedProducts(updatedProducts);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('No se encontró un token de autenticación. Por favor, inicie sesión nuevamente.');
        return;
      }
      
      // Obtener fecha/hora del negocio actualizada antes de enviar la venta
      let currentBusinessTime = businessDateTime;
      const selectedPointOfSale = pointsOfSale.find(pos => pos.id === formData.point_of_sale_id);
      if (selectedPointOfSale?.business_id) {
        console.log('Obteniendo fecha/hora actualizada del negocio para la venta detallada...');
        const freshBusinessTime = await fetchBusinessTime(selectedPointOfSale.business_id);
        if (freshBusinessTime) {
          currentBusinessTime = freshBusinessTime;
          console.log('Fecha/hora actualizada del negocio obtenida:', currentBusinessTime);
        } else {
          console.warn('No se pudo obtener la fecha/hora actualizada del negocio, usando valor anterior o fecha local');
        }
      }

      // Validar campos requeridos
      if (!formData.sale_date || !formData.total_amount || !formData.point_of_sale_id || !formData.payment_method || !formData.customer_id) {
        setError('Todos los campos son obligatorios');
        setLoading(false);
        return;
      }

      // Validar que el monto total sea un número positivo
      if (isNaN(formData.total_amount) || parseFloat(formData.total_amount) <= 0) {
        setError('El monto total debe ser un número positivo');
        setLoading(false);
        return;
      }

      // Validar que haya productos seleccionados
      if (selectedProducts.length === 0) {
        setError('Debe agregar al menos un producto a la venta');
        setLoading(false);
        return;
      }

      // Preparar los detalles de productos para enviar al servidor
      const productDetails = selectedProducts.map(product => ({
        product_id: product.id,
        quantity: product.quantity,
        price: product.prod_price,
        discount: product.discount,
        subtotal: (product.prod_price * product.quantity) - product.discount
      }));

      // Combinar la fecha seleccionada por el usuario con la zona horaria del negocio
      let finalSaleDate = formData.sale_date;
      
      try {
        // Si tenemos la fecha/hora del negocio y el usuario ha seleccionado una fecha
        if (currentBusinessTime && formData.sale_date) {
          console.log('Fecha seleccionada por el usuario (original):', formData.sale_date);
          
          // Extraer componentes de la fecha seleccionada por el usuario
          // Formato esperado: YYYY-MM-DD
          const [userYear, userMonth, userDay] = formData.sale_date.split('-').map(Number);
          
          // Crear objeto Date con la fecha/hora del negocio
          const businessDate = new Date(currentBusinessTime);
          console.log('Fecha/hora del negocio (original):', businessDate.toISOString());
          
          // Crear una nueva fecha combinando la fecha del usuario con la hora del negocio
          // IMPORTANTE: Usamos UTC para evitar problemas de zona horaria
          const combinedDate = new Date(Date.UTC(
            userYear,
            userMonth - 1, // Meses en JavaScript son 0-indexed
            userDay,
            businessDate.getUTCHours(),
            businessDate.getUTCMinutes(),
            businessDate.getUTCSeconds(),
            businessDate.getUTCMilliseconds()
          ));
          
          // Convertir a string ISO
          finalSaleDate = combinedDate.toISOString();
          console.log('Fecha combinada (UTC, fecha del usuario + hora del negocio):', finalSaleDate);
        } else {
          // Si no tenemos fecha/hora del negocio, usar la fecha seleccionada por el usuario
          console.log('Usando fecha seleccionada por el usuario:', formData.sale_date);
        }
      } catch (dateError) {
        console.error('Error al combinar fecha del usuario con zona horaria del negocio:', dateError);
        // En caso de error, usar la fecha seleccionada por el usuario como respaldo
      }

      // Enviar datos al servidor
      console.log('Registrando venta detallada con fecha/hora:', finalSaleDate);
      const response = await axios.post('/api/sales/detailed', 
        {
          sale_date: finalSaleDate,
          total_amount: parseFloat(formData.total_amount),
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          customer_id: formData.customer_id,
          products: productDetails,
          general_discount: generalDiscount,
          tax_rate: taxRate,
          subtotal: subtotal,
          grand_total: grandTotal
        }, 
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // Mostrar mensaje de éxito
      setSuccessMessage('Venta registrada correctamente');
      
      // Generar ticket si está marcada la opción
      if (printTicket) {
        console.log('Generando ticket de venta PDF...');
        
        // Preparar datos para el ticket
        const ticketData = {
          sale_date: finalSaleDate,
          payment_method: formData.payment_method,
          point_of_sale_id: formData.point_of_sale_id,
          products: selectedProducts.map(product => ({
            id: product.id,
            name: product.prod_name,
            quantity: product.quantity,
            price: product.prod_price,
            discount: product.discount || 0,
            subtotal: (product.prod_price * product.quantity) - (product.discount || 0)
          })),
          // Asegurar que el descuento sea un número válido para el ticket
          generalDiscount: typeof generalDiscount === 'string' ? 
            (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
            (typeof generalDiscount === 'number' ? generalDiscount : 0),
          general_discount: typeof generalDiscount === 'string' ? 
            (generalDiscount === '' || generalDiscount === '.' ? 0 : parseFloat(generalDiscount)) : 
            (typeof generalDiscount === 'number' ? generalDiscount : 0),
          taxRate: taxRate,
          tax_rate: taxRate,
          taxAmount: taxAmount,
          tax_amount: taxAmount,
          subtotal: subtotal,
          totalBeforeTax: totalBeforeTax,
          total_before_tax: totalBeforeTax,
          grandTotal: grandTotal,
          grand_total: grandTotal
        };
        
        // Generar y descargar el ticket
        const ticketGenerated = await generateTicket(ticketData);
        
        if (ticketGenerated) {
          console.log('Ticket generado y descargado correctamente');
        } else {
          console.error('Error al generar el ticket');
        }
      }

      // Limpiar el formulario
      setFormData({
        sale_date: new Date().toISOString().split('T')[0],
        total_amount: '',
        payment_method: 'Efectivo', // Mantener el valor por defecto
        point_of_sale_id: formData.point_of_sale_id, // Mantener el punto de venta seleccionado
        customer_id: formData.customer_id // Mantener el cliente seleccionado
      });
      setSelectedProducts([]);
      setGeneralDiscount(0.00);
      setTaxRate(0.00);
      setTaxAmount(0.00);
      setPrintTicket(false); // Resetear el estado del checkbox
      
      // Ocultar el mensaje de éxito después de 3 segundos
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);

      // Notificar al componente padre
      onSaleAdded();
    } catch (error) {
      console.error('Error al registrar la venta:', error);
      setError(error.response?.data?.message || 'Error al registrar la venta. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="detailed-sale-form-container">
      <h3>Venta Detallada</h3>
      {successMessage && <div className="success-message" style={{ backgroundColor: '#d4edda', color: '#155724', padding: '10px', borderRadius: '4px', marginBottom: '15px' }}>{successMessage}</div>}
      {error && <div className="error-message">{error}</div>}
      <form className="detailed-sale-form" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="point_of_sale_id">Punto de Venta *</label>
          <select
            id="point_of_sale_id"
            name="point_of_sale_id"
            value={formData.point_of_sale_id}
            onChange={handleChange}
            required
            disabled={loadingPointsOfSale || pointsOfSale.length <= 1}
          >
            <option value="">Seleccione un punto de venta</option>
            {pointsOfSale.map(pos => (
              <option key={pos.id} value={pos.id}>
                {pos.pos_name}
              </option>
            ))}
          </select>
          {loadingPointsOfSale && <div className="loading-indicator">Cargando puntos de venta...</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="customer_id">Cliente *</label>
          <select
            id="customer_id"
            name="customer_id"
            value={formData.customer_id}
            onChange={handleChange}
            required
            disabled={loadingCustomers}
          >
            <option value="">Seleccione un cliente</option>
            {customers.map(customer => (
              <option key={customer.id} value={customer.id}>
                {customer.cus_name}
              </option>
            ))}
          </select>
          {loadingCustomers && <div className="loading-indicator">Cargando clientes...</div>}
        </div>
        
        <div className="form-group">
          <label htmlFor="sale_date">Fecha de Venta *</label>
          <input
            type="date"
            id="sale_date"
            name="sale_date"
            value={formData.sale_date}
            onChange={handleChange}
            required
          />
        </div>
        
        {/* Sección de productos */}
        <div className="products-section">
          <h4>Productos</h4>
          
          <div className="add-product-form">
            <div className="product-search">
              <input
                type="text"
                placeholder="Buscar por código o nombre del producto..."
                value={productSearchTerm}
                onChange={handleProductSearchChange}
                className="product-search-input"
              />
            </div>
            <div className="product-selection">
              <select
                id="product_id"
                value={selectedProductId}
                onChange={handleProductChange}
                disabled={loadingProducts || filteredProducts.length === 0}
              >
                <option value="">Seleccione un producto</option>
                {filteredProducts.map(product => (
                  <option key={product.id} value={product.id}>
                    {product.prod_code ? `[${product.prod_code}] ` : ''}{product.prod_name} - ${product.prod_price.toFixed(2)}
                  </option>
                ))}
              </select>
              
              <div className="quantity-input">
                <label htmlFor="quantity">Cantidad:</label>
                <input
                  type="text"
                  id="quantity"
                  className="numeric-input"
                  placeholder="0.00"
                  value={productQuantity}
                  onChange={handleQuantityChange}
                  disabled={!selectedProductId}
                />
              </div>
              
              <div className="discount-input">
                <label htmlFor="discount">Descuento total ($):</label>
                <input
                  type="text"
                  id="discount"
                  value={productDiscount}
                  onChange={handleDiscountChange}
                  disabled={!selectedProductId}
                  className="numeric-input-no-arrows"
                  placeholder="0.00"
                />
              </div>
              
              <button
                type="button"
                className="add-product-btn"
                onClick={handleAddProduct}
                disabled={!selectedProductId}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="10" y1="11" x2="14" y2="11"></line>
                </svg>
                Agregar
              </button>
            </div>
          </div>
          
          {selectedProducts.length > 0 ? (
            <div className="selected-products">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Producto</th>
                    <th>Precio</th>
                    <th>Cantidad</th>
                    <th>Descuento</th>
                    <th>Subtotal</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedProducts.map(product => {
                    // Calcular el subtotal: (precio * cantidad) - descuento
                    const subtotal = (product.prod_price * product.quantity) - product.discount;
                    return (
                      <tr key={product.id}>
                        <td>{product.prod_name}</td>
                        <td>${product.prod_price.toFixed(2)}</td>
                        <td>
                          <div className="quantity-control">
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleUpdateQuantity(product.id, Math.max(0.01, (parseFloat(product.quantity) - 0.5)).toFixed(2))}
                              disabled={product.quantity <= 1}
                            >
                              -
                            </button>
                            <span>{parseFloat(product.quantity).toFixed(2)}</span>
                            <button
                              type="button"
                              className="quantity-btn"
                              onClick={() => handleUpdateQuantity(product.id, (parseFloat(product.quantity) + 0.5).toFixed(2))}
                            >
                              +
                            </button>
                          </div>
                        </td>
                        <td>
                          <div className="discount-control">
                            <input
                              type="text"
                              className="discount-input-field numeric-input-no-arrows"
                              value={product.discount}
                              onChange={(e) => {
                                // Obtener el valor del input
                                let value = e.target.value;
                                
                                // Validar que solo contenga números y un punto decimal
                                if (/^\d*\.?\d*$/.test(value) || value === '') {
                                  handleUpdateDiscount(product.id, value);
                                }
                              }}
                              placeholder="0.00"
                            />
                          </div>
                        </td>
                        <td>${subtotal.toFixed(2)}</td>
                        <td>
                          <button
                            type="button"
                            className="remove-product-btn"
                            onClick={() => handleRemoveProduct(product.id)}
                          >
                            Eliminar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="4" className="total-label">Subtotal:</td>
                    <td colSpan="2" className="total-amount">${subtotal.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="no-products-message">
              No hay productos agregados a la venta.
            </div>
          )}
        </div>
        
        {/* Sección de totales */}
        {selectedProducts.length > 0 && (
          <div className="totals-section">
            <div className="totals-grid">
              <div className="total-row">
                <div className="total-label">Descuento General ($):</div>
                <div className="total-input">
                  <input
                    type="text"
                    value={generalDiscount}
                    onChange={handleGeneralDiscountChange}
                    className="numeric-input-no-arrows"
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="total-row">
                <div className="total-label">Total antes de Impuesto:</div>
                <div className="total-value">${totalBeforeTax.toFixed(2)}</div>
              </div>
              <div className="total-row">
                <div className="total-label">Impuesto (%):</div>
                <div className="total-input">
                  <input
                    type="number"
                    value={taxRate.toFixed(2)}
                    onChange={handleTaxRateChange}
                    min="0"
                    step="0.01"
                  />
                </div>
              </div>
              <div className="total-row grand-total">
                <div className="total-label">Gran Total:</div>
                <div className="total-value">${grandTotal.toFixed(2)}</div>
              </div>
            </div>
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="payment_method">Método de Pago *</label>
          <div className="payment-method-options">
            <div className={`payment-option ${formData.payment_method === 'Efectivo' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_cash"
                name="payment_method"
                value="Efectivo"
                checked={formData.payment_method === 'Efectivo'}
                onChange={handleChange}
                required
              />
              <label htmlFor="payment_cash">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="12" rx="2" />
                  <circle cx="12" cy="12" r="2" />
                  <path d="M6 12h.01M18 12h.01" />
                </svg>
                Efectivo
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Tarjeta' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_card"
                name="payment_method"
                value="Tarjeta"
                checked={formData.payment_method === 'Tarjeta'}
                onChange={handleChange}
              />
              <label htmlFor="payment_card">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                  <line x1="1" y1="10" x2="23" y2="10" />
                </svg>
                Tarjeta
              </label>
            </div>
            <div className={`payment-option ${formData.payment_method === 'Transferencia' ? 'selected' : ''}`}>
              <input
                type="radio"
                id="payment_transfer"
                name="payment_method"
                value="Transferencia"
                checked={formData.payment_method === 'Transferencia'}
                onChange={handleChange}
              />
              <label htmlFor="payment_transfer">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                  <polyline points="16 3 12 7 8 3" />
                </svg>
                Transferencia
              </label>
            </div>
          </div>
          
          {/* Checkbox para generar ticket de venta */}
          <div className="checkbox-container" style={{ marginTop: '15px', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              id="print_ticket"
              checked={printTicket}
              onChange={(e) => setPrintTicket(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            <label htmlFor="print_ticket" className="checkbox-label" style={{ fontSize: '14px', cursor: 'pointer' }}>
              Generar Ticket de Venta (80mm)
            </label>
          </div>
        </div>
        
        {/* Mensaje de éxito */}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        
        <div className="form-buttons">
          <button type="button" className="cancel-btn" onClick={onCancel} disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="submit-btn" disabled={loading || selectedProducts.length === 0}>
            {loading ? 'Registrando...' : 'Registrar Venta'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default DetailedSaleForm;
